import type { InputHTMLAttributes, JSX, ReactNode } from 'react'
import { passwordStrength } from '@/lib/password'
import { Field } from './Field'

/**
 * A password input with the three-bar strength meter S-D2 drew.
 *
 * The bars are decorative and `aria-hidden`; the meaning is in the help text,
 * which states the level in words. That is the CLAUDE.md rule about colour, and
 * it is load-bearing here rather than pedantic: the difference between "Strong"
 * and "Too short" is the difference between submitting and being rejected, and
 * a red bar is invisible to a screen reader and to eight percent of men.
 *
 * `error` overrides the hint entirely — a server-side refusal is more important
 * than a client-side opinion about length, and showing both would put two
 * different judgements of the same field beside each other.
 */
export function PasswordField({
  label,
  error,
  ...input
}: {
  label: string
  error?: ReactNode
  value: string
} & Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'className' | 'id' | 'type' | 'value'
>): JSX.Element {
  const strength = passwordStrength(input.value)

  return (
    <Field label={label} error={error} required={input.required}>
      {(aria) => (
        <>
          <input className="ar-input" type="password" {...aria} {...input} />
          <div className="app-strength" data-level={strength.level || undefined}>
            <div className="app-strength__bars" aria-hidden="true">
              <i />
              <i />
              <i />
            </div>
            {error ? null : (
              <span className="ar-field__help">
                {strength.label ? <strong>{strength.label} — </strong> : null}
                {strength.hint}
              </span>
            )}
          </div>
        </>
      )}
    </Field>
  )
}
